import React, { useState, useEffect } from 'react';
import { Search, Filter, ExternalLink, ArrowRight, ChevronRight, X, Shield, Clock, Plane, MapPin } from 'lucide-react';
import Logo from '../../components/Logo';
import Footer from '../../components/Footer';
import ThemeToggle from '../../components/ThemeToggle';
import WalletMenu from '../../components/WalletMenu';
import { web3Service } from '../../lib/web3';
import NavigationMenu from '../../components/NavigationMenu';

interface Flight {
  id: string;
  callsign: string;
  aircraft_type: string;
  origin: string;
  destination: string;
  departure_time: string;
  arrival_time: string;
  status: 'scheduled' | 'in-flight' | 'landed' | 'delayed';
  transaction_hash: string;
  block_number: number;
  operator: string;
  co2_offset: {
    amount: number;
    unit: string;
    project: string;
    certificate_url: string;
  };
  nft?: {
    token_id: string;
    opensea_url: string;
    owner_address: string;
  };
}

const mockFlights: Flight[] = []; // All entries removed

export default function FlightTracker() {
  const [flights] = useState<Flight[]>(mockFlights);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [address, setAddress] = useState<string | null>(null);

  useEffect(() => {
    const currentAccount = web3Service.getAccount();
    setAddress(currentAccount);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'in-flight': return 'bg-green-100 text-green-800';
      case 'landed': return 'bg-gray-100 text-gray-800';
      case 'delayed': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredFlights = flights.filter(flight => {
    const searchLower = searchTerm.toLowerCase();
    return (
      flight.callsign.toLowerCase().includes(searchLower) ||
      flight.origin.toLowerCase().includes(searchLower) ||
      flight.destination.toLowerCase().includes(searchLower) ||
      flight.aircraft_type.toLowerCase().includes(searchLower)
    );
  });

  const showNoRecords = true;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-8xl mx-auto">
          <div className="flex items-center justify-between py-4 px-6">
            <div className="flex items-center gap-8">
              <Logo />
              <NavigationMenu />
            </div>
            <div className="flex items-center gap-4">
              <WalletMenu onShowDashboard={() => {}} />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 pt-[88px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Flight Tracker</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our mission is to bring unprecedented transparency to private aviation. By recording every flight on the
              blockchain with carbon offset certificates, we're creating an immutable record of private aviation activity worldwide.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-16">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by flight number, route, or aircraft type..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Filter size={20} className="text-gray-600" />
                </button>
              </div>
            </div>

            <div className="py-16 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <Plane size={32} className="text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No Records Available</h3>
              <p className="text-gray-600 max-w-md mx-auto">
                There are currently no flight records available in the system. Please check back later as new flights are added regularly.
              </p>
            </div>
          </div>
          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-50 p-8 rounded-2xl h-[300px] overflow-y-auto custom-scrollbar">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-4">
                <Shield size={24} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Blockchain Transparency</h3>
              <p className="text-gray-600">
                Every private flight is recorded on the blockchain, creating an immutable and verifiable record of aviation activity. This unprecedented level of transparency allows stakeholders to track and verify flight operations, ensuring accountability and trust in the private aviation sector.
              </p>
              <div className="mt-4">
                <h4 className="font-bold text-sm mb-2">Key Benefits:</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Immutable flight records</li>
                  <li>• Verifiable carbon offsets</li>
                  <li>• Public accessibility</li>
                  <li>• Decentralized storage</li>
                  <li>• Transparent operations</li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl h-[300px] overflow-y-auto custom-scrollbar">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-4">
                <Clock size={24} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Real-time Tracking</h3>
              <p className="text-gray-600">
                Monitor private flights worldwide in real-time with accurate departure and arrival information. Our advanced tracking system provides detailed insights into flight operations, enabling better planning and coordination.
              </p>
              <div className="mt-4">
                <h4 className="font-bold text-sm mb-2">Features:</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Live flight status updates</li>
                  <li>• Accurate arrival predictions</li>
                  <li>• Route visualization</li>
                  <li>• Historical flight data</li>
                  <li>• Custom alerts and notifications</li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl h-[300px] overflow-y-auto custom-scrollbar">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18.364 18.364A9 9 0 0 0 5.636 5.636"></path>
                  <path d="M12 18a6 6 0 0 0-6-6"></path>
                  <path d="M12 12a3 3 0 0 0-3-3"></path>
                  <line x1="12" y1="21" x2="12" y2="12"></line>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Carbon Offset Verification</h3>
              <p className="text-gray-600">
                All flights include verified carbon offset certificates stored on the blockchain. Each certificate is linked to specific environmental projects, allowing passengers to verify their contribution to carbon reduction.
              </p>
              <div className="mt-4">
                <h4 className="font-bold text-sm mb-2">Offset Projects:</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Reforestation initiatives</li>
                  <li>• Renewable energy development</li>
                  <li>• Ocean conservation</li>
                  <li>• Sustainable agriculture</li>
                  <li>• Carbon capture technology</li>
                </ul>
              </div>
            </div>
          </div>
                  {/* CTA Banner */}
          <div className="bg-black text-white rounded-2xl p-8 text-center shadow-lg">
            <h2 className="text-2xl md:text-3xl font-semibold mb-4">Do you have questions about flights, registration, monitoring or want to register your own flights?</h2>
            <p className="text-lg text-gray-300 mb-6 max-w-2xl mx-auto">
              Our team is here to help you get onboard with blockchain-based aviation monitoring and transparency.
            </p>
            <a
              href="https://privatecharterx.com/contact"
              className="inline-flex items-center gap-2 bg-black text-white font-semibold px-6 py-3 rounded-full hover:bg-gray-900 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline-block"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Contact Support
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
